import { AppDispatch, GetState, RootState } from '../store';
import { checkReportAPI } from '../../services/CheckReportService';
import qs from 'qs';
import { handleError } from '../slices/errors.slice';
import { UpdateReportError } from '../../types/check/errors';
import { initializeState } from './init-state';
import { initializeProcessingStep } from './init-current-step';
import { PersonaInformation } from '../slices/persona.slice';
import { selectDeactivatedConsentForwardingAndResults } from '../slices/features.slice';
import { AdditionalConsentNames } from '../../types/check';

interface SubmitPersonaInformationPayload {
	firstName: string;
	lastName: string;
	country?: string;
	dateOfBirth?: string;
	acceptTermsAndConditions: number;
	acceptPrivacyPolicy: number;
	status: string;
	additionalConsents?: string;
}

const ALREADY_PROCESSED_ERROR = 'Check is being processed, editing user data at this stage is not allowed';

export const putPersonaData = (persona: PersonaInformation) => {
	return async (dispatch: AppDispatch, getState: GetState) => {
		try {
			const state = getState();
			const payload = createFullPayload(persona, state);
			const response = await submitPersonaData(payload);

			if (response.data.errors) {
				dispatch(processUpdateErrors(response.data.errors));
				return;
			}

			dispatch(initializeProcessingStep());
		} catch (error: any) {
			dispatch(handleError(error));
		}
	};
};

function createFullPayload(persona: PersonaInformation, state: ReturnType<GetState>): SubmitPersonaInformationPayload {
	const deactivatedConsent = selectDeactivatedConsentForwardingAndResults(state);
	const basePayload = createPersonaPayload(persona, deactivatedConsent);
	const additionalConsentsData = getAdditionalConsents(state);

	return {
		...basePayload,
		additionalConsents: additionalConsentsData
	};
}

function createPersonaPayload(
	personaInformation: PersonaInformation,
	privacyDisabled: boolean
): SubmitPersonaInformationPayload {
	const { dateOfBirth, country, ...restPersonaInfo } = personaInformation;

	const filteredPersonaInfo: Pick<SubmitPersonaInformationPayload, 'dateOfBirth' | 'country'> = {};
	if (dateOfBirth !== '') filteredPersonaInfo.dateOfBirth = dateOfBirth;
	if (country !== '') filteredPersonaInfo.country = country;

	return {
		...restPersonaInfo,
		...filteredPersonaInfo,
		acceptTermsAndConditions: 1,
		acceptPrivacyPolicy: privacyDisabled ? 0 : 1,
		status: 'completed'
	};
}

function getAdditionalConsents(state: RootState): string | undefined {
	const { additionalConsents } = state.reports;
	if (!additionalConsents) return undefined;

	const acceptedConsents = getAcceptedAdditionalConsents(state);
	return JSON.stringify({
		token: additionalConsents,
		accepted: acceptedConsents
	});
}

function getAcceptedAdditionalConsents(state: RootState): Array<AdditionalConsentNames> {
	const acceptedConsents: Array<AdditionalConsentNames> = [];

	if (state.persona.consentAdditional1) {
		acceptedConsents.push('CONSENT_ADDITIONAL_1');
	}
	if (state.persona.consentAdditional2) {
		acceptedConsents.push('CONSENT_ADDITIONAL_2');
	}

	return acceptedConsents;
}

async function submitPersonaData(payload: SubmitPersonaInformationPayload) {
	return await checkReportAPI.putPersonaData(qs.stringify(payload));
}

function processUpdateErrors(errors: Array<string>) {
	return (dispatch: AppDispatch) => {
		const error = errors[0];

		if (error === ALREADY_PROCESSED_ERROR) {
			dispatch(initializeState());
			return;
		}

		dispatch(handleError(new UpdateReportError(errors)));
	};
}
